// Import via the `constants/*` path alias (bare 'constants' resolves to Node's built-in module in TS).
import * as Constants from 'constants/index';
import type { CostCenterInvite } from '@/types/cost-center-invite';

interface SelectToolAction {
  type: string;
  name: string;
}

interface ClearToolSelectionAction {
  type: string;
}

interface ToggleFilterAction {
  type: string;
  id: string;
}

interface ClearFiltersAction {
  type: string;
}

interface SelectLicenseRowAction {
  type: string;
  licenseId: string;
  costCenterId: string;
}

interface DeselectLicenseRowAction {
  type: string;
  licenseId: string;
}

interface SelectUserRowAction {
  type: string;
  // Domain shape of a user is defined in untyped modules; keep as a boundary type.
  user: unknown;
}

interface DeselectUserRowAction {
  type: string;
  user: unknown;
}

interface DeselectAllUserRowsAction {
  type: string;
}

interface SortPayload {
  columnName: string;
  desc: boolean;
}

interface SortAction {
  type: string;
  columnName: string;
  desc: boolean;
}

interface SelectSuffixRowAction {
  type: string;
  id: string;
}

interface DeselectSuffixRowAction {
  type: string;
  id: string;
}

interface SelectMultipleSuffixRowsAction {
  type: string;
  rows: unknown[];
}

interface DeselectMultipleSuffixRowsAction {
  type: string;
}

interface SelectLicenseDetailsAction {
  type: string;
  id: string;
}

interface SetCostCenterInvitesAction {
  type: string;
  invites: CostCenterInvite[];
}

interface SetCostCenterDetailsAction {
  type: string;
  costCenter: unknown;
}

interface SetAllCostCenterInvitesAction {
  type: string;
  invitesMap: Record<string, CostCenterInvite[]>;
}

interface SetCostCenterInvitesFetchErrorAction {
  type: string;
}

interface SetCostCenterInvitesCacheAction {
  type: string;
  invites: CostCenterInvite[];
  costCenterId: string;
}

interface SetCostCenterInvitesCacheFetchErrorAction {
  type: string;
  costCenterId: string;
}

interface FetchSearchUsersAction {
  type: string;
}

interface FetchSuccessAction {
  type: string;
}

interface FetchErrorAction {
  type: string;
  actions: unknown[];
}

export function selectTool(name: string): SelectToolAction {
  return {
    type: Constants.SuperAdmin.SELECTOR_ACTION_TYPES.SELECT_TOOL,
    name,
  };
}

export function clearToolSelection(): ClearToolSelectionAction {
  return { type: Constants.SuperAdmin.SELECTOR_ACTION_TYPES.CLEAR_TOOL_SELECTION };
}

export const toggleFilter = (id: string): ToggleFilterAction => ({
  type: Constants.SuperAdmin.FILTER_ACTION_TYPES.TOGGLE_FILTER,
  id,
});

export const clearFilters = (): ClearFiltersAction => ({
  type: Constants.SuperAdmin.FILTER_ACTION_TYPES.CLEAR_FILTERS,
});

export const selectLicenseRow = (
  licenseId: string,
  costCenterId: string,
): SelectLicenseRowAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SELECT_LICENSE_ROW,
  licenseId,
  costCenterId,
});

export const deselectLicenseRow = (licenseId: string): DeselectLicenseRowAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.DESELECT_LICENSE_ROW,
  licenseId,
});

export const selectUserRow = (user: unknown): SelectUserRowAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SELECT_USER_ROW,
  user,
});

export const deselectUserRow = (user: unknown): DeselectUserRowAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.DESELECT_USER_ROW,
  user,
});

export const deselectAllUserRows = (): DeselectAllUserRowsAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.DESELECT_ALL_USER_ROWS,
});

export const sort = ({ columnName, desc }: SortPayload): SortAction => ({
  type: Constants.SuperAdmin.SORT_ACTION_TYPES.SORT,
  columnName,
  desc,
});

export const sortSuffixes = ({ columnName, desc }: SortPayload): SortAction => ({
  type: Constants.SuperAdmin.SORT_ACTION_TYPES.SORT_SUFFIX,
  columnName,
  desc,
});

export const selectSuffixRow = (id: string): SelectSuffixRowAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SELECT_SUFFIX_ROW,
  id,
});

export const deselectSuffixRow = (id: string): DeselectSuffixRowAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.DESELECT_SUFFIX_ROW,
  id,
});

export const selectMultipleSuffixRows = (rows: unknown[]): SelectMultipleSuffixRowsAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SELECT_MULTIPLE_SUFFIX_ROW,
  rows,
});

export const deselectMultipleSuffixRows = (): DeselectMultipleSuffixRowsAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.DESELECT_MULTIPLE_SUFFIX_ROW,
});

export const selectLicenseDetails = (id: string): SelectLicenseDetailsAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SELECT_LICENSE_DETAILS,
  id,
});

export const setCostCenterInvites = (invites: CostCenterInvite[]): SetCostCenterInvitesAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SET_COSTCENTER_INVITES,
  invites,
});

export const setCostCenterDetails = (costCenter: unknown): SetCostCenterDetailsAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SET_COSTCENTER_DETAILS,
  costCenter,
});

export const setAllCostCenterInvites = (
  invitesMap: Record<string, CostCenterInvite[]>,
): SetAllCostCenterInvitesAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SET_ALL_COST_CENTER_INVITES,
  invitesMap,
});

export const setCostCenterInvitesFetchError = (): SetCostCenterInvitesFetchErrorAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SET_COST_CENTER_INVITES_FETCH_ERROR,
});

export const setCostCenterInvitesCache = (
  costCenterId: string,
  invites: CostCenterInvite[],
): SetCostCenterInvitesCacheAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SET_COST_CENTER_INVITES_CACHE,
  costCenterId,
  invites,
});

export const setCostCenterInvitesCacheFetchError = (
  costCenterId: string,
): SetCostCenterInvitesCacheFetchErrorAction => ({
  type: Constants.SuperAdmin.TABLE_ACTION_TYPES.SET_COST_CENTER_INVITES_CACHE_FETCH_ERROR,
  costCenterId,
});

export const fetchSearchUsers = (): FetchSearchUsersAction => ({
  type: Constants.SuperAdmin.FETCH_ACTION_TYPES.FETCH_SEARCH_USERS,
});

export const fetchSuccess = (): FetchSuccessAction => ({
  type: Constants.SuperAdmin.FETCH_ACTION_TYPES.FETCH_SUCCESS,
});

export const fetchError = (actions: unknown[] = []): FetchErrorAction => ({
  type: Constants.SuperAdmin.FETCH_ACTION_TYPES.FETCH_ERROR,
  actions,
});
